import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { FaArrowLeft, FaRegCalendarCheck } from "react-icons/fa6";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import { useAuth } from "../../../Contexts/AuthenticateProvider";
import { textCapitalize } from "../../../Helpers/Utils";
import { Skeleton } from "../../../Components/Needed";
import { listBackPath } from "../../../Helpers/useListParams";
import { LeaveCancelModal, cancellableDays, leaveTypeText } from "../../../Components/LeaveCancel";

const localizer = momentLocalizer(moment);
const PERMISSION_DAILY_LIMIT = 60;

// Same palette as the staff calendar (colour-blind safe), see ATTENDANCE_CALENDAR_SELECTORS doc.
const TONES = {
    PRESENT: { bg: "#0b7a4b", ink: "#ffffff", label: "Present" },
    ABSENT: { bg: "#d02a2a", ink: "#ffffff", label: "Absent" },
    HALF_DAY: { bg: "#e3a008", ink: "#1b1b23", label: "Half day" },
    LEAVE: { bg: "#2570cc", ink: "#ffffff", label: "Leave" },
    PERMISSION: { bg: "#851980", ink: "#ffffff", label: "Permission" },
};
const LEAVE_LABELS = { SICK_LEAVE: "Sick leave", CASUAL_LEAVE: "Casual leave", EMERGENCY_LEAVE: "Emergency leave", HALF_DAY: "Half day leave" };
const LOG_LABELS = { BREAK: "Break", LUNCH: "Lunch", ONDUTY: "Entry out / in (on duty)", PERSONAL: "Personal" };
const REQUEST_TONE = { PENDING: "warn", APPROVED: "ok", REJECTED: "bad", CANCELLED: "muted" };

const asHours = (minutes) => {
    if (!minutes) return "0m";
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return hours ? `${hours}h ${rest}m` : `${rest}m`;
};
// Backend dates are UTC midnights; build the matching local calendar date so nothing shifts a day.
const localDay = (value, endOfDay = false) => {
    const d = new Date(value);
    return endOfDay ? new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59) : new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};
const utcKey = (value) => new Date(value).toISOString().slice(0, 10);
const time = (value) => (value ? moment(value).format("hh:mm A") : "-");
const stamp = (value) => (value ? moment(value).format("DD MMM, hh:mm A") : "-");
const dayCount = (from, to) => Math.round((new Date(to) - new Date(from)) / 86400000) + 1;
// A leave row (so a half day can name its half); a bare type still works.
const leaveLabel = (value) => (typeof value === "object" && value ? leaveTypeText(value) : (LEAVE_LABELS[value] || textCapitalize(String(value || "").replace(/_/g, " ").toLowerCase())));

const StatusPill = ({ status }) => <span className={`att-pill att-${REQUEST_TONE[status] || "muted"}`}>{textCapitalize(String(status || "").toLowerCase())}</span>;

const AttendanceCalendar = () => {
    const { staffId } = useParams();
    const navigate = useNavigate();
    const [current, setCurrent] = useState(() => new Date());
    const [selected, setSelected] = useState(() => new Date());
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [checkOutTime, setCheckOutTime] = useState("");
    const [remarks, setRemarks] = useState("");
    const [endOpenLog, setEndOpenLog] = useState(false);
    const [saving, setSaving] = useState(false);
    const [checkInTime, setCheckInTime] = useState("");
    const [checkInReason, setCheckInReason] = useState("");
    const { hasPermission } = useAuth();
    const canManage = hasPermission("staff.attendance.manage");
    const embedded = false;

    const load = useCallback(async () => {
        setLoading(true);
        const y = current.getFullYear();
        const m = current.getMonth();
        const response = await Handler({
            method: "post",
            url: `/staff/team/attendance/${staffId}/calendar`,
            data: { from: new Date(Date.UTC(y, m, 1 - 7)).toISOString(), to: new Date(Date.UTC(y, m + 1, 7)).toISOString() },
        });
        if (response?.success) { setData(response?.data?.data); setError(""); } else { setData(null); setError(response?.data?.message || "Unable to load attendance"); }
        setLoading(false);
    }, [staffId, current]);

    useEffect(() => { load(); }, [load]);

    const openShift = data?.openShift || null;
    // Default the check-out time to now whenever a different open shift shows up.
    const openShiftId = openShift?.attendanceId;
    useEffect(() => {
        setCheckOutTime(moment().format("YYYY-MM-DDTHH:mm"));
        setRemarks("");
        setEndOpenLog(false);
    }, [openShiftId]);

    const submitCheckOut = async () => {
        if (!checkOutTime) return toast.error("Pick a check-out time");
        if (!remarks.trim()) return toast.error("Remarks is required");
        if (openShift?.activeLog && !endOpenLog) return toast.error("Tick the box to end the running break / entry with this check-out");
        setSaving(true);
        const response = await Handler({
            method: "patch",
            url: `/staff/team/attendance/${staffId}/checkout`,
            data: {
                checkOutTime: new Date(checkOutTime).toISOString(),
                attendanceId: openShift.attendanceId,
                ...(openShift.activeLog ? { endOpenLog: true } : {}),
                remarks: remarks.trim(),
            },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Checked out");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to check out"));
        }
        setSaving(false);
    };

    const todayKey = moment().format("YYYY-MM-DD");

    const events = useMemo(() => {
        if (!data) return [];
        const list = [];
        (data.days || []).forEach((day) => {
            const key = utcKey(day.date);
            if (key > todayKey || day.status === "LEAVE") return;
            if (day.status === "ABSENT" && key === todayKey) return;
            const tone = TONES[day.status] ? day.status : "PRESENT";
            list.push({
                id: `day-${key}`, kind: "day", tone, allDay: true, start: localDay(day.date), end: localDay(day.date, true),
                title: `${TONES[tone].label}${day.workingMinutes ? ` · ${asHours(day.workingMinutes)}` : ""}`,
            });
        });
        (data.leaves || []).filter((item) => !["REJECTED", "CANCELLED"].includes(item.status)).forEach((leave) => {
            list.push({
                id: leave.leaveId, kind: "leave", tone: "LEAVE", pending: leave.status === "PENDING", allDay: true,
                start: localDay(leave.fromDate), end: localDay(leave.toDate, true),
                title: `${leaveLabel(leave)}${leave.status === "PENDING" ? " (pending)" : ""}`,
            });
        });
        (data.permissions || []).filter((item) => item.status !== "REJECTED").forEach((permission) => {
            list.push({
                id: permission.permissionId, kind: "permission", tone: "PERMISSION", pending: permission.status === "PENDING", allDay: false,
                start: new Date(permission.fromTime), end: new Date(permission.toTime),
                title: `Permission ${permission.durationMinutes}m${permission.status === "PENDING" ? " (pending)" : ""}`,
            });
        });
        return list;
    }, [data, todayKey]);

    const eventStyle = (event) => {
        const tone = TONES[event.tone];
        return {
            style: {
                background: event.pending ? "#ffffff" : tone.bg,
                color: event.pending ? tone.bg : tone.ink,
                border: `1.5px ${event.pending ? "dashed" : "solid"} ${tone.bg}`,
                borderRadius: 6, fontSize: 12, padding: "1px 6px",
            },
        };
    };

    const dayStyle = (date) => (moment(date).format("YYYY-MM-DD") === moment(selected).format("YYYY-MM-DD") ? { style: { background: "#f1f5ff" } } : {});

    const selectedKey = moment(selected).format("YYYY-MM-DD");
    const day = useMemo(() => (data?.days || []).find((item) => utcKey(item.date) === selectedKey) || null, [data, selectedKey]);
    const dayLeaves = useMemo(() => (data?.leaves || []).filter((item) => utcKey(item.fromDate) <= selectedKey && utcKey(item.toDate) >= selectedKey), [data, selectedKey]);
    const dayPermissions = useMemo(() => (data?.permissions || []).filter((item) => moment(item.fromTime).format("YYYY-MM-DD") === selectedKey), [data, selectedKey]);
    const permissionUsed = dayPermissions.filter((item) => item.status !== "REJECTED").reduce((sum, item) => sum + (item.durationMinutes || 0), 0);

    // Check in on this person's behalf: only on a day with no shift logged, and never while a shift is open.
    // Leave applied for the day (pending or approved; a half-day leave still means they work) hides both buttons.
    const leaveApplied = dayLeaves.some((item) => !["REJECTED", "CANCELLED"].includes(item.status) && item.type !== "HALF_DAY");
    // Approved full-day leave on the day: a manager may still check the person in (the plan changed and the leave could
    // not be cancelled in time) - the record notes it - or cancel the leave so the person can check in themselves.
    const approvedLeaveToday = dayLeaves.find((item) => item.status === "APPROVED" && item.type !== "HALF_DAY") || null;
    // Check in: only today, nothing logged yet, no leave. A past day with no record is simply absent.
    const canCheckInHere = Boolean(canManage && !embedded && data && !openShift && !(leaveApplied && !approvedLeaveToday) && selectedKey === todayKey && !(day?.sessions?.length));
    const [cancelTarget, setCancelTarget] = useState(null);
    // Check out: a shift that really is open shows the button on its own day and every later day up to today,
    // so a forgotten check-out can be closed with a date and time.
    const canCheckOutHere = Boolean(canManage && !embedded && openShift && selectedKey >= utcKey(openShift.attendanceDate) && selectedKey <= todayKey);
    useEffect(() => {
        setCheckInTime(selectedKey === todayKey ? moment().format("YYYY-MM-DDTHH:mm") : `${selectedKey}T09:00`);
        setCheckInReason("");
    }, [selectedKey, todayKey]);

    const submitCheckIn = async () => {
        if (!checkInTime) return toast.error("Pick a check-in time");
        if (!checkInReason.trim()) return toast.error("A reason is required");
        setSaving(true);
        const response = await Handler({
            method: "post",
            url: `/staff/team/attendance/${staffId}/checkin`,
            data: { checkInTime: new Date(checkInTime).toISOString(), remarks: checkInReason.trim() },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Checked in");
            load();
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to check in"));
        }
        setSaving(false);
    };

    // Month figures for the header chips (only days inside the visible month).
    const monthStats = useMemo(() => {
        const out = { present: 0, absent: 0, half: 0, leave: 0, worked: 0, breaks: 0 };
        const prefix = moment(current).format("YYYY-MM");
        (data?.days || []).forEach((item) => {
            const key = utcKey(item.date);
            if (!key.startsWith(prefix) || key > todayKey) return;
            if (item.status === "PRESENT") out.present += 1;
            else if (item.status === "HALF_DAY") out.half += 1;
            else if (item.status === "LEAVE") out.leave += 1;
            else if (item.status === "ABSENT" && key !== todayKey) out.absent += 1;
            out.worked += item.workingMinutes || 0;
            out.breaks += item.breakMinutes || 0;
        });
        const permissions = (data?.permissions || []).filter((item) => item.status === "APPROVED" && moment(item.fromTime).format("YYYY-MM") === prefix);
        out.permissionMinutes = permissions.reduce((sum, item) => sum + (item.durationMinutes || 0), 0);
        return out;
    }, [data, current, todayKey]);

    const staff = data?.staff;
    const prefix = moment(current).format("YYYY-MM");
    const monthLeaves = (data?.leaves || []).filter((item) => utcKey(item.fromDate).startsWith(prefix) || utcKey(item.toDate).startsWith(prefix));
    const monthPermissions = (data?.permissions || []).filter((item) => moment(item.fromTime).format("YYYY-MM") === prefix);

    return (
        <div className="container-fluid px-0">
            <style>{`
                .acal .rbc-calendar { min-height: 640px; }
                .acal .rbc-toolbar button { font-size: 13px; }
                .acal .rbc-today { background: #fffbea; }
                .acal .rbc-off-range-bg { background: #f6f6f8; }
                .acal .rbc-date-cell { padding: 4px 6px; }
                .acal .rbc-event { outline: none; }
                .acal .legend-dot { width: 12px; height: 12px; border-radius: 3px; display: inline-block; margin-right: 6px; }
                .acal .log-row { border-left: 3px solid #d6d9e0; padding-left: 10px; margin-bottom: 8px; }
            `}</style>
            <button type="button" className="btn btn-link text-decoration-none px-0 mb-2 d-inline-flex align-items-center gap-2" onClick={() => navigate(listBackPath("/team/attend"))}><FaArrowLeft /> Back to team attendance</button>

            <div className="card border-0 shadow-sm rounded-4 mb-3">
                <div className="card-body p-4">
                    <h3 className="fw-bold mb-1 d-flex align-items-center gap-2 text-capitalize"><FaRegCalendarCheck className="text-primary" /> {staff ? textCapitalize(staff.name) : "Attendance"}</h3>
                    <p className="text-muted small mb-3">{staff ? [staff.employeeId || staff.staffId, staff.designation, staff.hubId].filter(Boolean).join(" · ") : ""} — click a day for the full detail.</p>
                    <div className="d-flex flex-wrap gap-2">
                        <span className="att-chip att-ok">Present<strong className="ms-2">{monthStats.present}</strong></span>
                        <span className="att-chip att-bad">Absent<strong className="ms-2">{monthStats.absent}</strong></span>
                        <span className="att-chip att-warn">Half day<strong className="ms-2">{monthStats.half}</strong></span>
                        <span className="att-chip att-info">Leave days<strong className="ms-2">{monthStats.leave}</strong></span>
                        <span className="att-chip att-muted">Worked<strong className="ms-2">{asHours(monthStats.worked)}</strong></span>
                        <span className="att-chip att-muted">Breaks<strong className="ms-2">{asHours(monthStats.breaks)}</strong></span>
                        <span className="att-chip att-muted">Permission<strong className="ms-2">{asHours(monthStats.permissionMinutes)}</strong></span>
                    </div>
                </div>
            </div>

            {error ? <div className="alert alert-danger">{textCapitalize(error)}</div> : null}

            <div className="row g-3 acal">
                <div className="col-12 col-xl-8">
                    <div className="card border-0 shadow-sm rounded-4">
                        <div className="card-body p-3">
                            <div className="d-flex flex-wrap gap-3 small mb-2">
                                {Object.keys(TONES).map((key) => <span key={key}><span className="legend-dot" style={{ background: TONES[key].bg }} />{TONES[key].label}</span>)}
                                <span><span className="legend-dot" style={{ background: "#fff", border: "1.5px dashed #555" }} />Pending request</span>
                            </div>
                            {loading && !data ? <Skeleton height={600} className="rounded-4" /> : (
                                <Calendar
                                    localizer={localizer}
                                    events={events}
                                    date={current}
                                    onNavigate={setCurrent}
                                    views={["month", "agenda"]}
                                    defaultView="month"
                                    selectable
                                    popup
                                    startAccessor="start"
                                    endAccessor="end"
                                    eventPropGetter={eventStyle}
                                    dayPropGetter={dayStyle}
                                    onSelectSlot={(slot) => setSelected(slot.start)}
                                    onSelectEvent={(event) => setSelected(event.start)}
                                    style={{ height: 640 }}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-12 col-xl-4">
                    {canCheckOutHere ? (
                        <div className="card border-0 shadow-sm rounded-4 mb-3" style={{ borderLeft: "4px solid #e3a008" }}>
                            <div className="card-body p-4">
                                <h6 className="fw-bold mb-1">Open shift — not checked out</h6>
                                <p className="small text-muted mb-3">
                                    Checked in <strong>{stamp(openShift.checkIn)}</strong>.
                                    {openShift.carriedOver ? " This shift started on an earlier day, so this person cannot check in again until it is closed." : ""}
                                </p>
                                <label className="form-label small fw-semibold">Check-out time</label>
                                <input type="datetime-local" className="form-control mb-3" value={checkOutTime} min={moment(openShift.checkIn).format("YYYY-MM-DDTHH:mm")} max={moment().format("YYYY-MM-DDTHH:mm")} onChange={(e) => setCheckOutTime(e.target.value)} />
                                {openShift.activeLog ? (
                                    <div className="form-check mb-3">
                                        <input id="endOpenLog" type="checkbox" className="form-check-input" checked={endOpenLog} onChange={(e) => setEndOpenLog(e.target.checked)} />
                                        <label htmlFor="endOpenLog" className="form-check-label small">
                                            A {LOG_LABELS[openShift.activeLog.type] || openShift.activeLog.type} (since {time(openShift.activeLog.checkOut)}) is still running. End it at this check-out time.
                                        </label>
                                    </div>
                                ) : null}
                                <label className="form-label small fw-semibold">Reason</label>
                                <textarea className="form-control mb-3" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Why this was closed manually" />
                                <button type="button" className="btn btn-primary w-100" onClick={submitCheckOut} disabled={saving}>{saving ? "Saving..." : "Check out"}</button>
                            </div>
                        </div>
                    ) : null}
                    {canCheckInHere ? (
                        <div className="card border-0 shadow-sm rounded-4 mb-3" style={{ borderLeft: "4px solid #0b7a4b" }}>
                            <div className="card-body p-4">
                                <h6 className="fw-bold mb-1">Not checked in yet today</h6>
                                <p className="small text-muted mb-3">Check this person in yourself. The time and a reason are recorded against your name.</p>
                                {approvedLeaveToday ? <div className="alert alert-warning py-2 small">This person has approved leave today. Checking them in is allowed and the record will say so. If they are working the whole day, cancel the leave from the Leave section below instead.</div> : null}
                                <label className="form-label small fw-semibold">Check-in time</label>
                                <input type="datetime-local" className="form-control mb-3" value={checkInTime} min={`${selectedKey}T00:00`} max={selectedKey === todayKey ? moment().format("YYYY-MM-DDTHH:mm") : `${selectedKey}T23:59`} onChange={(e) => setCheckInTime(e.target.value)} />
                                <label className="form-label small fw-semibold">Reason</label>
                                <textarea className="form-control mb-3" rows={2} value={checkInReason} onChange={(e) => setCheckInReason(e.target.value)} placeholder="Why you are checking them in" />
                                <button type="button" className="btn btn-success w-100" onClick={submitCheckIn} disabled={saving}>{saving ? "Saving..." : "Check in"}</button>
                            </div>
                        </div>
                    ) : null}
                    <div className="card border-0 shadow-sm rounded-4 mb-3">
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-start mb-3">
                                <div>
                                    <h5 className="fw-bold mb-0">{moment(selected).format("dddd, DD MMM YYYY")}</h5>
                                    <small className="text-muted">{selectedKey > todayKey ? "Upcoming day" : "Selected day"}</small>
                                </div>
                                {day && selectedKey <= todayKey && !(day.status === "ABSENT" && selectedKey === todayKey)
                                    ? <span className="badge rounded-pill" style={{ background: TONES[TONES[day.status] ? day.status : "PRESENT"].bg, color: TONES[TONES[day.status] ? day.status : "PRESENT"].ink }}>{TONES[day.status]?.label || textCapitalize(String(day.status || "").toLowerCase())}</span> : null}
                            </div>

                            <h6 className="fw-semibold small text-uppercase text-muted">Attendance</h6>
                            {!day?.sessions?.length ? <p className="text-muted small">{selectedKey > todayKey ? "Nothing yet." : "No check-in on this day."}</p> : null}
                            {(day?.sessions || []).map((session, index) => (
                                <div key={session.attendanceId || index} className="border rounded-3 p-3 mb-2">
                                    <div className="d-flex justify-content-between small mb-1">
                                        <strong>{day.sessions.length > 1 ? `Shift ${index + 1}` : "Shift"}{session.shift ? ` · ${textCapitalize(String(session.shift))}` : ""}</strong>
                                        <span className="text-muted">{session.source}</span>
                                    </div>
                                    <div className="small">In <strong>{time(session.checkIn)}</strong> → Out <strong>{session.checkOut ? <>{time(session.checkOut)}{moment(session.checkOut).format("YYYY-MM-DD") !== moment(session.checkIn).format("YYYY-MM-DD") ? <span className="att-nextday"> +1d</span> : null}</> : "still on shift"}</strong></div>
                                    <div className="small text-muted">Worked {asHours(session.totalWorkingMinutes)} · Breaks {asHours(session.totalBreakMinutes)}</div>
                                    {session.reason ? <div className="small text-muted mt-1">Check-in reason: {session.reason}</div> : null}
                                    {session.remarks ? <div className="small text-muted">Check-out reason: {session.remarks}</div> : null}
                                    {(session.logs || []).length ? (
                                        <div className="mt-2">
                                            {session.logs.map((log) => (
                                                <div key={log.logId} className="log-row small">
                                                    <strong>{LOG_LABELS[log.type] || log.type}</strong>
                                                    <div className="text-muted">
                                                        {time(log.checkOut)} → {log.checkIn ? time(log.checkIn) : <span className="text-warning">still running</span>}
                                                        {log.checkIn && log.checkOut ? ` (${asHours(Math.max(0, Math.floor((new Date(log.checkIn) - new Date(log.checkOut)) / 60000)))})` : ""}
                                                    </div>
                                                    {log.remarks ? <div className="text-muted">{log.remarks}</div> : null}
                                                </div>
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            ))}
                            {day?.sessions?.length ? <div className="small mb-3">Day total: <strong>{asHours(day.workingMinutes)}</strong> worked, <strong>{asHours(day.breakMinutes)}</strong> on breaks</div> : null}

                            <h6 className="fw-semibold small text-uppercase text-muted mt-3">Leave</h6>
                            {!dayLeaves.length ? <p className="text-muted small">No leave on this day.</p> : null}
                            {dayLeaves.map((leave) => (
                                <div key={leave.leaveId} className="border rounded-3 p-3 mb-2">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <strong className="small">{leaveLabel(leave)}</strong><StatusPill status={leave.status} />
                                    </div>
                                    <div className="small">{moment.utc(leave.fromDate).format("DD MMM")} – {moment.utc(leave.toDate).format("DD MMM YYYY")} · {dayCount(leave.fromDate, leave.toDate)} day{dayCount(leave.fromDate, leave.toDate) > 1 ? "s" : ""}</div>
                                    {leave.reason ? <div className="small text-muted">Reason: {leave.reason}</div> : null}
                                    <div className="small text-muted">Applied {stamp(leave.createdAt)}{leave.decidedBy ? ` · ${textCapitalize(leave.status.toLowerCase())} by ${leave.decidedBy}` : ""}</div>
                                    {leave.status === "CANCELLED" && leave.cancelReason ? <div className="small text-muted">Cancelled: {leave.cancelReason}</div> : null}
                                    {leave.cancelRequest?.status === "PENDING" ? <div className="small text-warning-emphasis">Cancel requested: {leave.cancelRequest.reason} (decide it in Time off)</div> : null}
                                    {hasPermission("staff.timeoff.decide") && leave.status === "APPROVED" && cancellableDays(leave) ? <button type="button" className="btn btn-sm btn-outline-danger mt-2" onClick={() => setCancelTarget(leave)}>Cancel leave</button> : null}
                                </div>
                            ))}

                            <h6 className="fw-semibold small text-uppercase text-muted mt-3">Permission</h6>
                            {!dayPermissions.length ? <p className="text-muted small">No permission on this day.</p> : null}
                            {dayPermissions.map((permission) => (
                                <div key={permission.permissionId} className="border rounded-3 p-3 mb-2">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <strong className="small">{textCapitalize(String(permission.permissionType || "").toLowerCase())} permission</strong><StatusPill status={permission.status} />
                                    </div>
                                    <div className="small">{time(permission.fromTime)} – {time(permission.toTime)} · {permission.durationMinutes} min</div>
                                    {permission.reason ? <div className="small text-muted">Reason: {permission.reason}</div> : null}
                                    <div className="small text-muted">Applied {stamp(permission.createdAt)}{permission.decidedBy ? ` · ${textCapitalize(permission.status.toLowerCase())} by ${permission.decidedBy}` : ""}</div>
                                </div>
                            ))}
                            {dayPermissions.length ? <div className="small">Permission used today: <strong>{permissionUsed} of {PERMISSION_DAILY_LIMIT} min</strong></div> : null}
                        </div>
                    </div>
                </div>

                <div className="col-12 col-lg-6">
                    <div className="card border-0 shadow-sm rounded-4 h-100">
                        <div className="card-body p-4">
                            <h6 className="fw-bold">Leave this month</h6>
                            {!monthLeaves.length ? <p className="text-muted small mb-0">No leave requests.</p> : (
                                <div className="table-responsive">
                                    <table className="table table-sm align-middle mb-0">
                                        <thead><tr className="text-muted small"><th>Type</th><th>Dates</th><th>Status</th><th>Reason</th></tr></thead>
                                        <tbody>
                                            {monthLeaves.map((leave) => (
                                                <tr key={leave.leaveId} role="button" onClick={() => setSelected(localDay(leave.fromDate))}>
                                                    <td className="small">{leaveLabel(leave)}</td>
                                                    <td className="small">{moment.utc(leave.fromDate).format("DD MMM")} – {moment.utc(leave.toDate).format("DD MMM")} ({dayCount(leave.fromDate, leave.toDate)}d)</td>
                                                    <td><StatusPill status={leave.status} /></td>
                                                    <td className="small text-muted">{leave.reason || "-"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-12 col-lg-6">
                    <div className="card border-0 shadow-sm rounded-4 h-100">
                        <div className="card-body p-4">
                            <h6 className="fw-bold">Permission this month</h6>
                            {!monthPermissions.length ? <p className="text-muted small mb-0">No permission requests.</p> : (
                                <div className="table-responsive">
                                    <table className="table table-sm align-middle mb-0">
                                        <thead><tr className="text-muted small"><th>Date</th><th>Time</th><th>Type</th><th>Status</th><th>Reason</th></tr></thead>
                                        <tbody>
                                            {monthPermissions.map((permission) => (
                                                <tr key={permission.permissionId} role="button" onClick={() => setSelected(new Date(permission.fromTime))}>
                                                    <td className="small">{moment(permission.fromTime).format("DD MMM")}</td>
                                                    <td className="small">{time(permission.fromTime)} – {time(permission.toTime)} ({permission.durationMinutes}m)</td>
                                                    <td className="small">{textCapitalize(String(permission.permissionType || "").toLowerCase())}</td>
                                                    <td><StatusPill status={permission.status} /></td>
                                                    <td className="small text-muted">{permission.reason || "-"}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {cancelTarget ? (
                <LeaveCancelModal
                    leave={cancelTarget}
                    mode="direct"
                    url={`/staff/team/leave/${cancelTarget.leaveId}/cancel`}
                    onClose={() => setCancelTarget(null)}
                    onDone={() => { setCancelTarget(null); load(); }}
                />
            ) : null}
        </div>
    );
};

export default AttendanceCalendar;
